import { useTranslation } from "react-i18next";
import { setLanguage, SUPPORTED_LANGS, type Lang } from "../i18n";
import { cx } from "./ui/cx";

/**
 * Compact language toggle: मराठी / English / हिंदी.
 * Renders as a segmented control on >=sm screens, dropdown on mobile.
 */
export function LangSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation("common");
  const current = (i18n.language?.split("-")[0] || "mr") as Lang;

  const labels: Record<Lang, string> = {
    en: t("lang.en"),
    mr: t("lang.mr"),
    hi: t("lang.hi"),
  };

  return (
    <>
      {/* Desktop / tablet — segmented */}
      <div
        role="radiogroup"
        aria-label={t("lang.label")}
        className={cx(
          "hidden overflow-hidden rounded-full border border-white/30 bg-white/10 text-[11px] font-medium sm:inline-flex",
          className
        )}
      >
        {SUPPORTED_LANGS.map((l) => {
          const active = l === current;
          return (
            <button
              key={l}
              role="radio"
              aria-checked={active}
              onClick={() => setLanguage(l)}
              className={cx(
                "px-2.5 py-1 transition-colors focus-ring",
                active
                  ? "bg-white text-[var(--c-brand-strong)]"
                  : "text-white hover:bg-white/15"
              )}
              lang={l === "en" ? "en" : l === "mr" ? "mr" : "hi"}
            >
              {labels[l]}
            </button>
          );
        })}
      </div>

      {/* Mobile — native select */}
      <label className="sm:hidden">
        <span className="sr-only">{t("lang.label")}</span>
        <select
          value={current}
          onChange={(e) => setLanguage(e.target.value as Lang)}
          className="min-h-tap rounded-full border border-white/30 bg-white/10 px-2 py-1 text-xs text-white focus:outline-none focus:ring-2 focus:ring-white/50"
        >
          {SUPPORTED_LANGS.map((l) => (
            <option key={l} value={l}>{labels[l]}</option>
          ))}
        </select>
      </label>
    </>
  );
}
